import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/backend";
import { type AvailabilitySlot } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { useMe } from "@/lib/hooks/use-me";
import { logger } from "@/logger";
import { getUtcStartOfDay } from "@/utils/time";
import { AvailabilitySlotCard } from "./availability-slot.card";

interface AvailabilitySlotListProps {
  projectId: string;
  showApplications?: boolean;
}

export function AvailabilitySlotList({
  projectId,
  showApplications = false,
}: AvailabilitySlotListProps) {
  const { t } = useTranslation();
  const { me } = useMe();
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);

  const { data: myUserFunctions, isLoading: isLoadingMyUserFunctions } = useQuery({
    queryKey: ["userFunction", me?.id],
    queryFn: () => extractionWrapper(api.userFunction.getMyFunctions()),
    enabled: !!me?.id,
  });

  const defaultQuery = {
    filter: {
      "project.id": {
        operator: "equals",
        value: projectId,
      },
      status: {
        operator: "equals",
        value: "PUBLISHED",
      },
      startAt: {
        operator: "gte",
        value: getUtcStartOfDay(),
      },
    },
    include: {
      userFunction: {
        select: true,
      },
    },
    sort: {
      field: "startAt",
      order: "asc",
    },
    onlyShowOpenSlots: true,

    aggregate: {
      acceptedApplications: {
        compute: true,
      },
    },

    pagination: {
      page: 1,
      take: 9999,
    },
  } satisfies Parameters<typeof api.availabilitySlot.getAll>[0];

  const { data: slotsForUserFunction } = useQuery({
    queryKey: [
      "availabilitySlots",
      "forUserFunction",
      projectId,
      defaultQuery,
      myUserFunctions?.map((userFunction) => userFunction.name),
    ],
    queryFn: () =>
      extractionWrapper(
        api.availabilitySlot.getAll({
          ...defaultQuery,
          filter: {
            ...defaultQuery.filter,
            "userFunction.name": {
              operator: "in",
              values: myUserFunctions?.map((userFunction) => userFunction.name) ?? [],
            },
          },
        }),
      ),
    enabled: !!projectId && !!myUserFunctions?.length,
  });

  const { data: slotsWithoutUserFunction } = useQuery({
    queryKey: [
      "availabilitySlots",
      "withoutUserFunction",
      projectId,
      defaultQuery,
      myUserFunctions,
    ],
    queryFn: () =>
      extractionWrapper(
        api.availabilitySlot.getAll({
          ...defaultQuery,
          filter: {
            ...defaultQuery.filter,
            userFunction: {
              operator: "notExists",
            },
          },
        }),
      ),
    enabled: !!projectId && !isLoadingMyUserFunctions,
  });

  const availabilitySlots: AvailabilitySlot[] = [
    ...(slotsForUserFunction?.items ?? []),
    ...(slotsWithoutUserFunction?.items ?? []),
  ].sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  const { data: applications, refetch: refetchApplications } = useQuery({
    queryKey: [
      "applications",
      projectId,
      me?.employee?.id,
      availabilitySlots?.map((slot) => slot.id),
    ],
    queryFn: () =>
      extractionWrapper(
        api.application.getAll({
          filter: {
            "employee.id": {
              operator: "equals",
              value: me?.employee?.id ?? "",
            },
            "availabilitySlot.id": {
              operator: "in",
              values: availabilitySlots?.map((slot) => slot.id) ?? [],
            },
          },
          pagination: {
            page: 1,
            take: 9999,
          },
        }),
      ),
    enabled: !!projectId && !!me?.employee?.id && availabilitySlots?.length > 0,
  });

  const mutationApply = useExtractedMutation({
    mutationKey: ["availability-slot", "apply"],
    mutationFn: api.application.createEmployeeApplication,
  });

  const availabilitySlotsByApplication = availabilitySlots?.filter((slot) => {
    const hasExistingApplication = applications?.items?.some(
      (app) => app.availabilitySlotId === slot.id,
    );
    return !hasExistingApplication;
  });

  const handleSlotSelect = (slotId: string) => {
    setSelectedSlots((prev) => {
      if (prev.includes(slotId)) {
        return prev.filter((id) => id !== slotId);
      }
      return [...prev, slotId];
    });
  };

  const handleSubmit = async () => {
    try {
      await mutationApply.mutateAsync({
        availabilitySlotIds: selectedSlots,
      });
      toast.success(t("actions:apply:success"));
      setSelectedSlots([]);
      await refetchApplications();
    } catch (error) {
      toast.error(t("actions:apply:error"));
      logger.error({ error }, "Error applying availability slots");
    }
  };

  const hasReserveSlots = selectedSlots.some((slotId) => {
    const slot = availabilitySlots.find((s) => s.id === slotId);
    return slot && slot.allowReserves && (slot.stats?.acceptedApplications ?? 0) >= slot.capacity;
  });

  if (!availabilitySlotsByApplication?.length) {
    return <p className="text-txt-secondary">{t("common:no-availability-slots")}</p>;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {availabilitySlotsByApplication.map((slot) => (
          <AvailabilitySlotCard
            key={slot.id}
            slot={slot}
            onSelect={() => handleSlotSelect(slot.id)}
            isSelected={selectedSlots.includes(slot.id)}
          />
        ))}
      </div>
      {selectedSlots.length > 0 && (
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleSubmit}
            className="w-full bg-brand-500"
            disabled={mutationApply.isPending}
          >
            {mutationApply.isPending
              ? t("common:submitting")
              : hasReserveSlots
                ? t("common:apply-reserve")
                : t("common:apply")}
          </Button>
        </div>
      )}
    </div>
  );
}
